import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Clock, Search, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';

const WhatsInProgress = () => {
  const { user } = useOutletContext();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/client-projects');
        setProjects(res.data);
      } catch (error) {
        toast.error('Failed to load projects');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const inProgress = useMemo(() => {
    const now = new Date();
    const oneWeekFromNow = new Date(now);
    oneWeekFromNow.setDate(now.getDate() + 7);

    let list = projects.filter(p => {
      if (p.task_status !== 'in progress') return false;
      if (!p.due_date) return false;
      const d = new Date(p.due_date);
      if (isNaN(d)) return false;
      // Only show due within the next 7 days from today (not old ones)
      return d >= now && d <= oneWeekFromNow;
    });
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(p =>
        Object.values(p).some(v => String(v).toLowerCase().includes(q))
      );
    }
    return list.sort((a, b) => {
      const da = a.due_date || '';
      const db = b.due_date || '';
      return da.localeCompare(db);
    });
  }, [projects, searchQuery]);

  // Count due today
  const dueTodayCount = useMemo(() => {
    const today = new Date().toDateString();
    return inProgress.filter(p => {
      const d = new Date(p.due_date);
      return d.toDateString() === today;
    }).length;
  }, [inProgress]);

  if (loading) {
    return (
      <DashboardLayout user={user}>
        <div className="flex items-center justify-center h-96">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout user={user}>
      <div className="space-y-6">
        <div>
          <h1 className="text-4xl font-heading font-bold accent-gradient" data-testid="in-progress-title">What's in Progress</h1>
          <p className="text-muted-foreground">{inProgress.length} projects due in the next 7 days</p>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-3xl font-bold text-blue-600">{inProgress.length}</p>
              <p className="text-xs text-muted-foreground">Due This Week</p>
            </CardContent>
          </Card>
          {dueTodayCount > 0 && (
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="pt-4 pb-3 text-center">
                <p className="text-3xl font-bold text-amber-600">{dueTodayCount}</p>
                <p className="text-xs text-muted-foreground">Due Today</p>
              </CardContent>
            </Card>
          )}
          <Card className="border-l-4 border-l-gray-300">
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-3xl font-bold text-gray-600">{projects.filter(p => p.task_status === 'in progress').length}</p>
              <p className="text-xs text-muted-foreground">Total In Progress</p>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Filter in-progress projects..."
            className="pl-10"
            data-testid="in-progress-search"
          />
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {inProgress.length === 0 ? (
              <div className="py-16 text-center">
                <Clock className="w-12 h-12 mx-auto text-muted-foreground/30 mb-3" />
                <p className="text-lg font-medium">All Clear!</p>
                <p className="text-muted-foreground text-sm">No projects due in the next 7 days</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10 text-center">#</TableHead>
                      <TableHead className="text-center whitespace-nowrap">Due Date</TableHead>
                      <TableHead className="text-center whitespace-nowrap">Main Title</TableHead>
                      <TableHead className="text-center whitespace-nowrap">Episode Title</TableHead>
                      <TableHead className="text-center whitespace-nowrap">Task</TableHead>
                      <TableHead className="text-center whitespace-nowrap">Language</TableHead>
                      <TableHead className="text-center whitespace-nowrap">Resource</TableHead>
                      <TableHead className="text-center whitespace-nowrap">QTY</TableHead>
                      <TableHead className="text-center whitespace-nowrap">Office</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inProgress.map((p, i) => {
                      const isToday = p.due_date && new Date(p.due_date).toDateString() === new Date().toDateString();
                      return (
                        <TableRow key={p.project_id || i} className={isToday ? 'bg-amber-50' : ''}>
                          <TableCell className="text-center text-muted-foreground text-xs">{i + 1}</TableCell>
                          <TableCell className="text-center text-sm whitespace-nowrap">
                            <span className={isToday ? 'text-amber-600 font-semibold' : 'font-medium'}>
                              {p.due_date || '—'}
                            </span>
                            {isToday && <AlertTriangle className="w-3 h-3 inline ml-1 text-amber-500" />}
                          </TableCell>
                          <TableCell className="text-center text-sm">{p.main_title || '—'}</TableCell>
                          <TableCell className="text-center text-sm">{p.episode_title || '—'}</TableCell>
                          <TableCell className="text-center text-sm">{p.task || '—'}</TableCell>
                          <TableCell className="text-center text-sm">{p.language || '—'}</TableCell>
                          <TableCell className="text-center text-sm">{p.resource || '—'}</TableCell>
                          <TableCell className="text-center text-sm">{p.qty > 0 ? p.qty : '—'}</TableCell>
                          <TableCell className="text-center text-sm">{p.office || '—'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default WhatsInProgress;
